import React from "react";
import { ClassNameProps, Column, makeClassName, Row, StyleProps, Tooltip } from "@hex-insights/core";
import {
	BigEventCalendar,
	BigEventCalendarView,
	EventCalendar,
	EventCalendarTooltipProps,
} from "../../../../Components/EventCalendar";
import { Icon } from "../../../../Components/Icon";
import {
	AcademicTermsDatesListQuery,
	CourseSectionPeriodFilterInput,
	CourseSectionPeriodScheduleListQuery,
	useAcademicTermsDatesListQuery,
} from "../../../../Utilities";
import { AcademicTermFormat, CourseSectionFormat, RoomFormat } from "../../../Format";
import { AcademicTermLink, CourseSectionLink, CourseSectionPeriodLink, RoomLink } from "../../../Links";
import { AcademicTermUtilities, CourseSectionPeriodUtilities } from "../../../Utilities";
import styles from "./styles.module.css";

const scheduleCalendarViews: BigEventCalendarView[] = ["week", "day"];

export type ScheduleCalendarProps = {
	initialDate?: Date;
	filters: CourseSectionPeriodFilterInput;
} & Partial<ClassNameProps & StyleProps>;

export function ScheduleCalendar({ initialDate, filters, className, style }: ScheduleCalendarProps) {
	const { value, onChange, firstOfMonth, lastOfMonth } = EventCalendar.useDates(initialDate);

	const { data: academicTermsData } = useAcademicTermsDatesListQuery();
	const { data } = CourseSectionPeriodUtilities.useCalendarScheduleQuery(firstOfMonth, lastOfMonth, filters);

	const events = React.useMemo(
		() => [
			...AcademicTermUtilities.toEventCalendarItems(academicTermsData?.academicTermConnection.edges),
			...CourseSectionPeriodUtilities.toEventCalendarItems(
				data?.courseSectionPeriodConnection.edges,
				firstOfMonth,
				lastOfMonth,
			),
		],
		[academicTermsData, data, firstOfMonth, lastOfMonth],
	);

	return (
		<BigEventCalendar
			value={value}
			onChange={onChange}
			events={events}
			initialView="week"
			views={scheduleCalendarViews}
			EventTooltip={EventTooltip}
			noItemIcon
			className={className}
			style={style}
		/>
	);
}

function EventTooltip(props: EventCalendarTooltipProps) {
	if (!!props.item._additional?.period) {
		return <ScheduleCalendarPeriodTooltip {...props} />;
	}
	if (!!props.item._additional?.academicTerm) {
		return <ScheduleCalendarAcademicTermTooltip {...props} />;
	}
	return null;
}

function ScheduleCalendarAcademicTermTooltip({ item, noStem, className, style }: EventCalendarTooltipProps) {
	const academicTerm = item._additional
		.academicTerm as AcademicTermsDatesListQuery["academicTermConnection"]["edges"][0]["node"];

	return (
		<Tooltip
			noStem={noStem}
			isInteractable
			className={makeClassName(styles["schedule-calendar__tooltip"], className)}
			style={style}
		>
			<Tooltip.Header>
				<AcademicTermLink
					instance={academicTerm}
					className={styles["schedule-calendar__tooltip__title"] + " link--no-text-decoration"}
				>
					{item.name}
				</AcademicTermLink>
			</Tooltip.Header>
			<Tooltip.Body>
				<Column justify="spaced-start">
					<Row justify="spaced-start" align="center" className={styles["schedule-calendar__tooltip__detail--main"]}>
						<Icon.CalendarBlank />
						<span>
							{AcademicTermFormat.Fields.startDate(academicTerm.startDate)} to{" "}
							{AcademicTermFormat.Fields.endDate(academicTerm.endDate)}
						</span>
					</Row>
				</Column>
			</Tooltip.Body>
		</Tooltip>
	);
}

function ScheduleCalendarPeriodTooltip({ item, noStem, className, style }: EventCalendarTooltipProps) {
	const period = item._additional
		.period as CourseSectionPeriodScheduleListQuery["courseSectionPeriodConnection"]["edges"][0]["node"];
	const startTime = new Date(item.startTime ?? "");
	const endTime = new Date(item.endTime ?? "");

	return (
		<Tooltip
			noStem={noStem}
			isInteractable
			className={makeClassName(styles["schedule-calendar__tooltip"], className)}
			style={style}
		>
			<Tooltip.Header>
				<CourseSectionLink
					instance={period.courseSection}
					className={styles["schedule-calendar__tooltip__title"] + " link--no-text-decoration"}
				>
					{CourseSectionFormat.name(period.courseSection)}
				</CourseSectionLink>
			</Tooltip.Header>
			<Tooltip.Body>
				<Column justify="spaced-start">
					<CourseSectionPeriodLink instance={period} className="link--no-text-decoration">
						<Row justify="spaced-start" align="center" className={styles["schedule-calendar__tooltip__detail--main"]}>
							<Icon.Clock />
							<span>
								{startTime.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" })} to{" "}
								{endTime.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" })}
							</span>
						</Row>
					</CourseSectionPeriodLink>

					{!!period.room && (
						<RoomLink instance={period.room} className="link--no-text-decoration">
							<Row justify="spaced-start" align="center" className={styles["schedule-calendar__tooltip__detail--main"]}>
								<Icon.MapPin /> {RoomFormat.name(period.room)}
							</Row>
						</RoomLink>
					)}
				</Column>
			</Tooltip.Body>
		</Tooltip>
	);
}
